import {
  ArrowLeft,
  CheckCircle2,
  CirclePlus,
  Filter,
  Network,
  Search,
  ShieldCheck,
  X,
} from "lucide-react";
import { useMemo, useState, type FormEvent } from "react";
import { devTrace } from "../../services/devTrace";
import type { Metric } from "../../types";

interface MetricsCatalogPageProps {
  metrics: Metric[];
  onChange: (metrics: Metric[]) => void;
}

interface MetricForm {
  displayName: string;
  name: string;
  description: string;
  domain: Metric["domain"];
  owner: string;
  source: string;
  table: string;
  expression: string;
  aggregation: string;
  unit: string;
  grain: string;
  dimensions: string;
  synonyms: string;
}

const emptyMetric: MetricForm = {
  displayName: "",
  name: "",
  description: "",
  domain: "Corporate Finance",
  owner: "",
  source: "Snowflake — Corporate Analytics",
  table: "",
  expression: "",
  aggregation: "Sum",
  unit: "Number",
  grain: "",
  dimensions: "",
  synonyms: "",
};

export function MetricsCatalogPage({ metrics, onChange }: MetricsCatalogPageProps) {
  const [creating, setCreating] = useState(false);
  const [selectedId, setSelectedId] = useState(metrics[0]?.id ?? "");
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState("All");
  const [form, setForm] = useState<MetricForm>(emptyMetric);
  const [formNotice, setFormNotice] = useState("");

  const filtered = useMemo(
    () =>
      metrics.filter((metric) => {
        const matchesQuery = `${metric.displayName} ${metric.name} ${metric.description}`
          .toLowerCase()
          .includes(query.toLowerCase());
        return matchesQuery && (status === "All" || metric.status === status);
      }),
    [metrics, query, status],
  );
  const selected = metrics.find((metric) => metric.id === selectedId) ?? filtered[0];

  function update(field: keyof MetricForm, value: string) {
    setForm((current) => ({ ...current, [field]: value }));
    setFormNotice("");
  }

  function createMetric(event: FormEvent) {
    event.preventDefault();
    if (
      !form.displayName ||
      !form.name ||
      !form.description ||
      !form.owner ||
      !form.table ||
      !form.expression ||
      !form.grain
    ) {
      setFormNotice("Complete all required fields before saving the draft.");
      return;
    }
    if (!/^[a-z][a-z0-9_]{2,79}$/.test(form.name)) {
      setFormNotice("Technical name must be lowercase snake_case.");
      return;
    }
    if (metrics.some((metric) => metric.name === form.name)) {
      setFormNotice("A metric with this technical name already exists.");
      return;
    }

    const metric: Metric = {
      id: crypto.randomUUID(),
      ...form,
      dimensions: form.dimensions.split(",").map((item) => item.trim()).filter(Boolean),
      synonyms: form.synonyms.split(",").map((item) => item.trim()).filter(Boolean),
      status: "Draft",
      updatedAt: new Date().toISOString(),
    };
    onChange([metric, ...metrics]);
    setSelectedId(metric.id);
    setCreating(false);
    setForm(emptyMetric);
    devTrace.record({
      runId: metric.id,
      step: "metric_draft_created",
      actor: "Metrics Catalog UI",
      level: "success",
      summary: `${metric.displayName} was saved as a draft metric.`,
      rationale:
        "New metrics remain drafts until backend validation, review, compilation, and publication are connected.",
      inputs: { form },
      output: { metricId: metric.id, status: metric.status },
    });
  }

  if (creating) {
    return (
      <section className="panel metric-form-panel">
        <div className="panel-heading">
          <div>
            <button type="button" className="back-button" onClick={() => setCreating(false)}>
              <ArrowLeft size={17} />
              Metrics Catalog
            </button>
            <h1>Create a new metric</h1>
            <p>Define business meaning, source, calculation, grain, and discoverability.</p>
          </div>
          <span className="status-badge draft">Draft workflow</span>
        </div>

        <form className="metric-form" onSubmit={createMetric}>
          <fieldset>
            <legend>Business definition</legend>
            <div className="form-grid">
              <label>
                Display name *
                <input value={form.displayName} onChange={(e) => update("displayName", e.target.value)} required />
              </label>
              <label>
                Technical name *
                <input
                  value={form.name}
                  onChange={(e) => update("name", e.target.value)}
                  placeholder="gross_margin_pct"
                  required
                />
              </label>
              <label className="span-two">
                Description *
                <textarea value={form.description} onChange={(e) => update("description", e.target.value)} rows={3} required />
              </label>
              <label>
                Domain *
                <select value={form.domain} onChange={(e) => update("domain", e.target.value)}>
                  <option>Corporate Finance</option>
                  <option>Program Management</option>
                  <option>Proposals</option>
                </select>
              </label>
              <label>
                Business owner *
                <input value={form.owner} onChange={(e) => update("owner", e.target.value)} required />
              </label>
            </div>
          </fieldset>

          <fieldset>
            <legend>Snowflake source and calculation</legend>
            <div className="form-grid">
              <label>
                Datasource *
                <input value={form.source} onChange={(e) => update("source", e.target.value)} required />
              </label>
              <label>
                Table or view *
                <input
                  value={form.table}
                  onChange={(e) => update("table", e.target.value)}
                  placeholder="FINANCE.PRODUCT_PERFORMANCE"
                  required
                />
              </label>
              <label className="span-two">
                Metric expression *
                <textarea
                  value={form.expression}
                  onChange={(e) => update("expression", e.target.value)}
                  placeholder="(SUM(REVENUE) - SUM(COGS)) / NULLIF(SUM(REVENUE), 0) * 100"
                  rows={3}
                  required
                />
                <small>Backend validation will allow-list functions and source members.</small>
              </label>
              <label>
                Aggregation *
                <select value={form.aggregation} onChange={(e) => update("aggregation", e.target.value)}>
                  <option>Sum</option>
                  <option>Average</option>
                  <option>Count</option>
                  <option>Count distinct</option>
                  <option>Calculated ratio</option>
                  <option>Calculated amount</option>
                </select>
              </label>
              <label>
                Unit and format *
                <select value={form.unit} onChange={(e) => update("unit", e.target.value)}>
                  <option>Number</option>
                  <option>Percent</option>
                  <option>USD</option>
                  <option>Hours</option>
                  <option>Days</option>
                </select>
              </label>
              <label>
                Metric grain *
                <input
                  value={form.grain}
                  onChange={(e) => update("grain", e.target.value)}
                  placeholder="Product · month"
                  required
                />
              </label>
              <label>
                Allowed dimensions
                <input
                  value={form.dimensions}
                  onChange={(e) => update("dimensions", e.target.value)}
                  placeholder="Product, Category, Fiscal Month"
                />
              </label>
            </div>
          </fieldset>

          <fieldset>
            <legend>AI Analyst context</legend>
            <label>
              Synonyms and business phrases
              <input
                value={form.synonyms}
                onChange={(e) => update("synonyms", e.target.value)}
                placeholder="margin, product profitability, gross profit percentage"
              />
            </label>
          </fieldset>

          {formNotice && <div className="inline-notice" role="alert">{formNotice}</div>}
          <div className="form-actions">
            <button type="button" className="button secondary" onClick={() => setCreating(false)}>
              Cancel
            </button>
            <button type="submit" className="button primary">
              <CirclePlus size={17} />
              Save draft metric
            </button>
          </div>
        </form>
      </section>
    );
  }

  return (
    <div className="content-stack">
      <section className="hero metrics-hero">
        <div>
          <span className="section-kicker">Governed semantic library</span>
          <h1>Metrics Catalog</h1>
          <p>
            Reuse certified metrics or create a structured draft for review. Every
            dashboard value should resolve to one approved definition.
          </p>
        </div>
        <button type="button" className="button light" onClick={() => setCreating(true)}>
          <CirclePlus size={18} />
          Create new metric
        </button>
      </section>

      <section className="panel catalog-layout">
        <div className="catalog-list">
          <div className="catalog-tools">
            <label className="search-field">
              <Search size={17} />
              <input
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Search metrics"
                aria-label="Search metrics"
              />
              {query && (
                <button type="button" onClick={() => setQuery("")} aria-label="Clear search">
                  <X size={15} />
                </button>
              )}
            </label>
            <label className="filter-field">
              <Filter size={16} />
              <select value={status} onChange={(event) => setStatus(event.target.value)} aria-label="Filter by status">
                <option>All</option>
                <option>Certified</option>
                <option>Review</option>
                <option>Draft</option>
              </select>
            </label>
          </div>
          <div className="metric-list">
            {filtered.map((metric) => (
              <button
                type="button"
                key={metric.id}
                className={selected?.id === metric.id ? "selected" : ""}
                onClick={() => setSelectedId(metric.id)}
              >
                <span className={`metric-status-mark ${metric.status.toLowerCase()}`}>
                  {metric.status === "Certified" ? <CheckCircle2 size={16} /> : <Network size={16} />}
                </span>
                <span><strong>{metric.displayName}</strong><small>{metric.domain} · {metric.grain}</small></span>
                <span className={`status-badge ${metric.status.toLowerCase()}`}>{metric.status}</span>
              </button>
            ))}
          </div>
        </div>

        {selected ? (
          <article className="metric-detail">
            <div className="metric-detail-title">
              <div>
                <span className={`status-badge ${selected.status.toLowerCase()}`}>{selected.status}</span>
                <h2>{selected.displayName}</h2>
                <code>{selected.name}</code>
              </div>
              {selected.status === "Certified" && <ShieldCheck size={26} className="success-color" />}
            </div>
            <p className="metric-description">{selected.description}</p>

            <div className="definition-grid">
              <div><span>Owner</span><strong>{selected.owner}</strong></div>
              <div><span>Domain</span><strong>{selected.domain}</strong></div>
              <div><span>Grain</span><strong>{selected.grain}</strong></div>
              <div><span>Unit</span><strong>{selected.unit}</strong></div>
              <div><span>Aggregation</span><strong>{selected.aggregation}</strong></div>
              <div><span>Source</span><strong>{selected.source}</strong></div>
            </div>

            <h3>Governed expression</h3>
            <pre>{selected.expression}</pre>

            <h3>Lineage</h3>
            <div className="lineage">
              <span>Snowflake</span><i>→</i><span>{selected.table}</span><i>→</i><span>BCL</span><i>→</i><span>Cube member</span>
            </div>

            <h3>Allowed dimensions</h3>
            <div className="chips">
              {selected.dimensions.map((dimension) => <span key={dimension}>{dimension}</span>)}
            </div>

            <h3>AI Analyst synonyms</h3>
            <div className="chips subtle">
              {selected.synonyms.map((synonym) => <span key={synonym}>{synonym}</span>)}
            </div>
          </article>
        ) : (
          <div className="empty-state"><Search size={28} /><strong>No metrics found</strong></div>
        )}
      </section>
    </div>
  );
}
